/**
 * Guard > Blacklist — CRUD de patterns (DLLs/dirs/exes) bloqueados pela
 * liberty.dll. Quando o client detecta um match na pasta do jogo, autoban
 * eh disparado via /internal/guard/autoban-blacklist.
 *
 * Acessada como sub-tab de /admin/guard (Guard tem: Sessoes / Banidos / Blacklist).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2, AlertCircle, Plus, Search, Edit2, Trash2, X, Power,
  FileWarning, FolderX, Terminal,
} from 'lucide-react';
import {
  listBlacklist, createBlacklist, updateBlacklist, deleteBlacklist,
  type BlacklistRow,
} from '@/shared/services/adminService';
import { useToast } from '@/shared/contexts/ToastContext';
import { useConfirm } from '@/shared/components/ui/ConfirmDialog';
import { DataTable, type DataTableColumn } from '@/shared/components/ui/DataTable';
import { RowActions } from '@/shared/components/ui/RowActions';

const KINDS = [
  { id: 1, label: 'DLL',  icon: <FileWarning size={11} />, color: '#fa3a3a' },
  { id: 2, label: 'Dir',  icon: <FolderX     size={11} />, color: '#a855f7' },
  { id: 3, label: 'EXE',  icon: <Terminal    size={11} />, color: '#ffd700' },
] as const;

function kindMeta(k: number) {
  return KINDS.find((x) => x.id === k) ?? KINDS[0];
}

const blacklistCols = (
  onToggle: (r: BlacklistRow) => void,
  onEdit: (r: BlacklistRow) => void,
  onDelete: (r: BlacklistRow) => void,
): DataTableColumn<BlacklistRow>[] => [
  {
    key: 'kind', label: 'Tipo', width: '80px',
    cell: (r) => {
      const k = kindMeta(r.kind);
      return (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold w-fit"
          style={{ background: `${k.color}1a`, color: k.color, border: `1px solid ${k.color}40` }}
        >
          {k.icon} {k.label}
        </span>
      );
    },
  },
  {
    key: 'pattern', label: 'Pattern', width: '1.5fr',
    cell: (r) => (
      <span className="text-sm font-mono text-white truncate" title={r.pattern}>
        {r.pattern}
      </span>
    ),
  },
  {
    key: 'notes', label: 'Notas', width: '2fr', mobileHidden: true,
    cell: (r) => (
      <span className="text-xs text-white/50 truncate" title={r.notes ?? ''}>
        {r.notes ?? '—'}
      </span>
    ),
  },
  {
    key: 'enabled', label: 'Status', width: '80px',
    cell: (r) => r.enabled ? (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold
                       bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
        Ativo
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold
                       bg-white/5 text-white/40 border border-white/10">
        Off
      </span>
    ),
  },
  {
    key: 'createdDate', label: 'Criado', width: '100px', mobileHidden: true,
    cell: (r) => (
      <span className="text-[11px] text-white/45 font-mono">
        {r.createdDate ? new Date(r.createdDate).toLocaleDateString('pt-BR') : '—'}
      </span>
    ),
  },
  {
    key: 'actions', label: 'Ações', width: '60px', align: 'right', mobileHidden: true,
    cell: (r) => (
      <RowActions
        actions={[
          {
            label: r.enabled ? 'Desativar' : 'Ativar',
            icon: <Power size={13} />,
            onClick: () => onToggle(r),
            variant: 'primary',
          },
          {
            label: 'Editar',
            icon: <Edit2 size={13} />,
            onClick: () => onEdit(r),
            variant: 'warning',
          },
          {
            label: 'Remover',
            icon: <Trash2 size={13} />,
            onClick: () => onDelete(r),
            variant: 'danger',
          },
        ]}
      />
    ),
  },
];

export default function GuardBlacklistPage() {
  const { notify } = useToast();
  const confirm = useConfirm();

  const [items, setItems] = useState<BlacklistRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState<number | null>(null);
  const [editing, setEditing] = useState<BlacklistRow | null>(null);
  const [creating, setCreating] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listBlacklist({
        search: search || undefined,
        kind: kindFilter ?? undefined,
      });
      setItems(rows);
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Erro ao carregar blacklist');
    } finally {
      setLoading(false);
    }
  }, [search, kindFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totals = useMemo(() => {
    const t = { all: items.length, dll: 0, dir: 0, exe: 0, disabled: 0 };
    items.forEach((i) => {
      if (!i.enabled) t.disabled += 1;
      if (i.kind === 1) t.dll += 1;
      if (i.kind === 2) t.dir += 1;
      if (i.kind === 3) t.exe += 1;
    });
    return t;
  }, [items]);

  const handleToggle = async (row: BlacklistRow) => {
    try {
      await updateBlacklist(row.id, {
        pattern: row.pattern, kind: row.kind, notes: row.notes,
        enabled: !row.enabled,
      });
      notify('success', `${row.pattern} ${row.enabled ? 'desativado' : 'ativado'}`);
      fetchData();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    }
  };

  const handleDelete = async (row: BlacklistRow) => {
    const ok = await confirm({
      title: `Remover '${row.pattern}'?`,
      description: 'Patterns removidos nao bloqueiam mais a DLL. Pra desativar temporariamente, prefira o toggle.',
      tone: 'danger',
      confirmLabel: 'Remover',
    });
    if (!ok) return;
    try {
      await deleteBlacklist(row.id);
      notify('success', 'Pattern removido');
      fetchData();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    }
  };

  return (
    <div>
      {/* Header — actions */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3 text-xs text-white/65">
          <StatPill color="#00e1d7" label="Total" value={totals.all} />
          <StatPill color={KINDS[0].color} label="DLLs" value={totals.dll} />
          <StatPill color={KINDS[1].color} label="Dirs" value={totals.dir} />
          <StatPill color={KINDS[2].color} label="EXEs" value={totals.exe} />
          {totals.disabled > 0 && (
            <StatPill color="#6b7280" label="Desativados" value={totals.disabled} />
          )}
        </div>
        <button
          onClick={() => setCreating(true)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                     bg-[#00e1d7]/15 border border-[#00e1d7]/30 text-[#00e1d7]
                     hover:bg-[#00e1d7]/25 transition cursor-pointer"
        >
          <Plus size={14} /> Cadastrar
        </button>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por pattern..."
            className="w-full bg-[#161e26] border border-white/[0.06] rounded-lg pl-9 pr-3 py-2
                       text-sm text-white placeholder:text-white/40
                       focus:outline-none focus:border-[#00e1d7]/40 transition"
          />
        </div>
        <div className="flex gap-1">
          <KindFilter cur={kindFilter} onChange={setKindFilter} />
        </div>
      </div>

      {error && (
        <div className="surface-card rounded-xl p-4 mb-4 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <DataTable<BlacklistRow>
        columns={blacklistCols(handleToggle, setEditing, handleDelete)}
        rows={items}
        rowKey={(r) => r.id}
        loading={loading}
        empty="Nenhum pattern cadastrado"
        mobileTitle={(r) => (
          <span className="flex items-center gap-2 font-mono">
            {r.pattern}
          </span>
        )}
        mobileSubtitle={(r) => r.notes || ''}
        mobileActions={(r) => (
          <RowActions
            actions={[
              { label: r.enabled ? 'Desativar' : 'Ativar', icon: <Power size={13} />,
                onClick: () => handleToggle(r), variant: 'primary' },
              { label: 'Editar', icon: <Edit2 size={13} />,
                onClick: () => setEditing(r), variant: 'warning' },
              { label: 'Remover', icon: <Trash2 size={13} />,
                onClick: () => handleDelete(r), variant: 'danger' },
            ]}
            align="left"
            
          />
        )}
      />

      {/* Modal de criacao/edicao */}
      {(creating || editing) && (
        <BlacklistFormModal
          initial={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); fetchData(); }}
        />
      )}
    </div>
  );
}

/* ──────────────── helpers ──────────────── */

function StatPill({ color, label, value }: { color: string; label: string; value: number }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-semibold"
      style={{ background: `${color}15`, color, border: `1px solid ${color}30` }}
    >
      {label} <span className="font-bold">{value}</span>
    </span>
  );
}

function KindFilter({ cur, onChange }: { cur: number | null; onChange: (v: number | null) => void }) {
  const opts = [
    { v: null, l: 'Todos' },
    { v: 1, l: 'DLL' },
    { v: 2, l: 'Dir' },
    { v: 3, l: 'EXE' },
  ];
  return (
    <div className="flex bg-[#161e26] border border-white/[0.06] rounded-lg p-0.5 text-xs">
      {opts.map((o) => (
        <button
          key={String(o.v)}
          onClick={() => onChange(o.v)}
          className={`px-3 py-1.5 rounded-md transition cursor-pointer ${
            cur === o.v
              ? 'bg-[#00e1d7]/15 text-[#00e1d7]'
              : 'text-white/55 hover:text-white'
          }`}
        >
          {o.l}
        </button>
      ))}
    </div>
  );
}

function BlacklistFormModal({
  initial, onClose, onSaved,
}: {
  initial: BlacklistRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { notify } = useToast();
  const [pattern, setPattern] = useState(initial?.pattern ?? '');
  const [kind, setKind] = useState(initial?.kind ?? 1);
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = pattern.trim();
    if (!trimmed) {
      notify('error', 'Pattern vazio');
      return;
    }
    setSaving(true);
    try {
      if (initial) {
        await updateBlacklist(initial.id, {
          pattern: trimmed, kind, notes: notes || null, enabled: initial.enabled,
        });
        notify('success', 'Pattern atualizado');
      } else {
        await createBlacklist({ pattern: trimmed, kind, notes: notes || null, enabled: true });
        notify('success', 'Pattern cadastrado');
      }
      onSaved();
    } catch (err: any) {
      const msg = err?.detail || err?.message || 'Erro ao salvar';
      notify('error', typeof msg === 'string' ? msg : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <form
        onSubmit={handleSubmit}
        className="relative z-10 w-full max-w-md rounded-2xl border border-[#00e1d7]/20
                   bg-[#0a1218]/95 backdrop-blur-xl shadow-[0_0_60px_rgba(0,225,215,0.08)]"
      >
        <div className="flex items-start justify-between p-5 border-b border-white/5">
          <div>
            <h3 className="text-lg font-extrabold text-white">
              {initial ? 'Editar pattern' : 'Cadastrar pattern'}
            </h3>
            <p className="text-xs text-white/55 mt-0.5">
              Nome de DLL, executável ou diretório a bloquear
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-white/55 hover:text-white hover:bg-white/5 transition cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-bold text-white/65 uppercase tracking-wider mb-2 block">
              Tipo
            </label>
            <div className="grid grid-cols-3 gap-2">
              {KINDS.map((k) => (
                <button
                  type="button"
                  key={k.id}
                  onClick={() => setKind(k.id)}
                  className={`p-2 rounded-lg border-2 text-xs font-bold transition cursor-pointer flex items-center justify-center gap-1.5 ${
                    kind === k.id ? '' : 'opacity-50'
                  }`}
                  style={{
                    background: `${k.color}15`,
                    borderColor: kind === k.id ? `${k.color}aa` : `${k.color}30`,
                    color: k.color,
                  }}
                >
                  {k.icon} {k.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-bold text-white/65 uppercase tracking-wider mb-2 block">
              Pattern <span className="text-red-400">*</span>
            </label>
            <input
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              placeholder={kind === 2 ? 'ex: Cheat Engine' : kind === 3 ? 'ex: trainer.exe' : 'ex: cabalhack.dll'}
              required
              className="w-full bg-[#161e26] border border-white/[0.06] rounded-lg px-3 py-2.5
                         text-sm font-mono text-white placeholder:text-white/40
                         focus:outline-none focus:border-[#00e1d7]/40 transition"
            />
            <p className="text-[10px] text-white/45 mt-1">
              {kind === 1 && 'Nome exato do arquivo .dll (case-insensitive)'}
              {kind === 2 && 'Nome da pasta (sem barras)'}
              {kind === 3 && 'Nome exato do .exe'}
            </p>
          </div>

          <div>
            <label className="text-xs font-bold text-white/65 uppercase tracking-wider mb-2 block">
              Notas
            </label>
            <input
              value={notes ?? ''}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Opcional — origem, referência, contexto"
              className="w-full bg-[#161e26] border border-white/[0.06] rounded-lg px-3 py-2.5
                         text-sm text-white placeholder:text-white/40
                         focus:outline-none focus:border-[#00e1d7]/40 transition"
            />
          </div>
        </div>

        <div className="px-5 py-4 border-t border-white/5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-white/5 border border-white/10 text-white/65 hover:bg-white/10 transition cursor-pointer"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-bold bg-[#00e1d7]/15 border border-[#00e1d7]/40 text-[#00e1d7] hover:bg-[#00e1d7]/25 transition cursor-pointer disabled:opacity-40"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : initial ? 'Salvar' : 'Cadastrar'}
          </button>
        </div>
      </form>
    </div>
  );
}
